import { bbox } from '@turf/turf';
import { Map, PaddingOptions, PointLike } from 'mapbox-gl';

/** GeoJSON inputs that may carry nullable geometry until narrowed at runtime. */
export type ZoomableGeoJson =
  | GeoJSON.FeatureCollection
  | GeoJSON.Feature
  | {
      type: 'Feature';
      geometry?: GeoJSON.Geometry | null;
      properties?: GeoJSON.GeoJsonProperties;
      id?: string | number;
      bbox?: GeoJSON.BBox;
    };

type Params = {
  geojson?: ZoomableGeoJson;
  map?: Map;
  animate?: boolean;
  padding?: number | PaddingOptions;
  offset?: PointLike;
};

const isValidBbox = ([x1, y1, x2, y2]: number[]) =>
  [x1, y1, x2, y2].every(Number.isFinite) &&
  x1 >= -180 &&
  x2 <= 180 &&
  y1 >= -90 &&
  y2 <= 90 &&
  x1 <= x2 &&
  y1 <= y2;

export const hasZoomableGeometry = (geojson?: ZoomableGeoJson) => {
  if (!geojson) {
    return false;
  }

  if (geojson.type === 'Feature') {
    return geojson.geometry != null;
  }

  if (geojson.type === 'FeatureCollection') {
    return geojson.features.some((feature) => feature.geometry != null);
  }

  return false;
};

export const zoomOnFeature = ({
  geojson,
  map,
  animate = true,
  padding = 50,
  offset = [0, 0],
}: Params) => {
  if (!geojson || !map || !hasZoomableGeometry(geojson)) {
    return;
  }

  const bounds = bbox(geojson as GeoJSON.FeatureCollection | GeoJSON.Feature);

  if (!isValidBbox(bounds)) {
    return;
  }

  const [x1, y1, x2, y2] = bounds;

  map.fitBounds([x1, y1, x2, y2], {
    padding: padding,
    animate,
    maxDuration: 1500,
    speed: 1.5,
    offset,
  });
};
